import { z } from 'zod';
import { ContentVisibility, PedagogicContentState } from '@sphere/shared/domain';
import { ResourceType } from '@sphere/school/domain';

const resourceLinkSchema = z
  .object({
    chapterId: z.string().uuid().optional(),
    lessonId: z.string().uuid().optional(),
  })
  .refine(
    d => (!!d.chapterId && !d.lessonId) || (!d.chapterId && !!d.lessonId),
    { message: 'Exactement un des deux (chapterId ou lessonId) doit être fourni' }
  );

/**
 * Schéma pour la création/édition d'une ressource
 * Supporte plusieurs liens (chapitres et/ou leçons) via links[]
 */
export const resourceSchema = z
  .object({
    title: z.string().min(1, 'Le titre est requis').max(255),
    description: z.string().optional(),
    type: z.nativeEnum(ResourceType, {
      message: 'Le type de ressource est requis',
    }),
    url: z.string().optional(),
    chapterId: z.string().optional(),
    lessonId: z.string().optional(),
    links: z.array(resourceLinkSchema).optional(),
    state: z.nativeEnum(PedagogicContentState).optional(),
    visibility: z.nativeEnum(ContentVisibility).optional(),
  })
  .refine(
    data => {
      const hasLinks = data.links && data.links.length > 0;
      const hasChapter = data.chapterId && data.chapterId.trim() !== '';
      const hasLesson = data.lessonId && data.lessonId.trim() !== '';
      return hasLinks || hasChapter || hasLesson;
    },
    {
      message:
        'Vous devez associer la ressource à au moins un chapitre ou une leçon',
      path: ['chapterId'],
    }
  )
  .refine(
    data => {
      // Si links: tous doivent être du même type (chapitres OU leçons, pas mélangés)
      if (data.links && data.links.length > 0) {
        const hasChapters = data.links.some(l => l.chapterId);
        const hasLessons = data.links.some(l => l.lessonId);
        return !(hasChapters && hasLessons);
      }
      const hasChapter = data.chapterId && data.chapterId.trim() !== '';
      const hasLesson = data.lessonId && data.lessonId.trim() !== '';
      return !(hasChapter && hasLesson);
    },
    {
      message:
        "Une ressource ne peut être liée qu'à des chapitres OU des leçons (pas les deux)",
      path: ['lessonId'],
    }
  );

export type ResourceFormData = z.infer<typeof resourceSchema>;
